import { ComponentType, createElement, lazy } from 'react';
import { Envs, Modules } from 'scbk-services/config';
import { PageComponentType } from 'scbk-services/types/page/page';
import { AuthorizableRouteObject } from 'scbk-services/types/route/route';
import { AuthStatus } from '../constants';

type EagerModule = {
    default: ComponentType<any>;
};
type LazyModuleLoader = () => Promise<EagerModule>;
//type ModuleValue = EagerModule | LazyModuleLoader;
type ModuleRegistry = Record<string, LazyModuleLoader>;

/**
 * AuthorizableRouteObject 빌더 클래스
 * pages 하위에 업무 화면 tsx를 모두 가져와..
 */
export class AuthRouteObjectBuilder {
    /**
     *
     * @param modules
     */
    constructor(
        private modules: ModuleRegistry,
        private module: string | Modules,
        private readonly fallback?: React.ReactNode,
    ) {}

    /**
     *
     * @param filePath
     * @returns
     */
    private toPath(filePath: string): string {
        return filePath
            .replace('../pages', '')
            .replace(/\/index\.tsx$/, '/')
            .replace(/\.tsx$/, '');
    }

    /**
     *
     * @param path
     * @returns
     */
    private toFullPath(path: string): string {
        return `/${this.module}${path}`;
    }

    /**
     *
     * @param filePath
     * @returns
     */
    private toRouteId(filePath: string): string {
        return filePath
            .split('/')
            .pop()!
            .replace(/\.tsx$/, '');
    }

    /**
     * pages 하위 '.tsx' 파일경로에 대한 필터링 처리를 수행한다.
     * @param filePath pages 하위 tsx 파일 경로
     * @returns
     */
    private filtered(filePath: string): boolean {
        //const excludedDirectory = ['/components/', '/hooks/', '/utils/', '/stores/'];
        const excludedDirectory = ['/components/', '/stores/'];
        //pages 하위 components 디렉토리는 제외
        if (excludedDirectory.some((dir) => filePath.includes(dir))) {
            return false;
        }
        return filePath.endsWith('.tsx');
    }
    /**
     *
     * @returns
     */
    public build(): AuthorizableRouteObject[] {
        const entries = Object.entries(this.modules).filter(([filePath, _]) =>
            this.filtered(filePath),
        );

        const routeObjects = entries.map(([filePath, moduleLoader]) => {
            const module = this.module;
            const path = this.toPath(filePath);
            const fullPath = this.toFullPath(path);
            const routeId = this.toRouteId(filePath);

            let modulePromise: ReturnType<typeof moduleLoader> | undefined;
            const loadModule = () => {
                modulePromise ??= moduleLoader();
                return modulePromise;
            };

            const LazyComponent = lazy(async () => {
                const loadedModule = await loadModule();

                if (!loadedModule || !loadedModule?.default) {
                    console.warn(
                        'AuthRouteObjectBuilder > LazyComponent - not found loadedModule : ',
                        {
                            loadedModule,
                            default: loadedModule?.default,
                        },
                    );
                    if (
                        import.meta.env.VITE_ENVIRONMENT === Envs.LOCAL ||
                        import.meta.env.VITE_ENVIRONMENT === Envs.DEV
                    ) {
                        debugger;
                    }
                }

                let returnDefault;
                if (
                    import.meta.env.VITE_ENVIRONMENT === Envs.LOCAL ||
                    import.meta.env.VITE_ENVIRONMENT === Envs.DEV
                ) {
                    returnDefault = loadedModule.default;
                } else {
                    returnDefault = loadedModule.default ?? this.fallback;
                }

                return {
                    default: returnDefault,
                };
            });

            const routeObject: AuthorizableRouteObject = {
                module,
                id: routeId,
                path,
                fullPath,
                //requiredAuthLevel: AuthStatus.UNAUTHENTICATED,
                //options: undefined,
                //meta: {},
                element: createElement(LazyComponent),
                resolve: async () => {
                    const loadedModule = await moduleLoader();
                    const Component = loadedModule?.default as PageComponentType<any>;

                    const pageId = Component.id ?? '';
                    const options = Component.options;
                    const requiredAuthLevel =
                        options?.requiredAuthLevel ?? AuthStatus.UNAUTHENTICATED;
                    const meta = options?.layoutOptions ?? {};

                    const resolved = {
                        module,
                        id: pageId,
                        path,
                        fullPath,
                        requiredAuthLevel,
                        options,
                        meta,
                    };
                    console.log(`# resolve id=${pageId}`, resolved);
                    return resolved;
                },
            };

            return routeObject;
        });
        return routeObjects.filter(Boolean);
    }
}
